import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import {
	compose,
	withState,
	withHandlers,
	lifecycle,
	withProps,
} from 'recompose';
import { TrackingClient } from '@sparefoot/tracking-client';
import './YoutubeVideo.scss';

const enhance = compose(
	withState('videoDuration', 'setVideoDuration', 0),
	withState('playedTime', 'setPlayedTime', 0),
	withState('hasSentEvent', 'setHasSentEvent', false),
	withState('player', 'setPlayer', null),
	withState('interval', 'setIntervalId', null),
	withProps({ iframeRef: createRef() }),
	withHandlers({
		trackVideoEvent:
			({ videoDuration, playedTime, videoName, setHasSentEvent }) =>
			() => {
				setHasSentEvent(true);
				TrackingClient.trackVideoWatched({
					video_duration: videoDuration,
					video_duration_played: playedTime,
					video_name: videoName,
				});
			},
	}),
	withHandlers({
		onStateChange:
			({
				player,
				setPlayedTime,
				interval,
				setIntervalId,
				hasSentEvent,
				trackVideoEvent,
			}) =>
			(event) => {
				// This method is called anytime the video state changes
				// playing, paused, ended, buffering, unstarted
				const playerState = window.YT.PlayerState;
				if (event.data === playerState.PLAYING && !interval) {
					const newInterval = setInterval(() => {
						if (player) {
							setPlayedTime(player.getCurrentTime());
						}
					}, 1000);
					setIntervalId(newInterval);
				} else if (player?.getCurrentTime() > 10 && !hasSentEvent) {
					// If the video has played more than 10 seconds, and we haven't sent a tracking evnet yet
					// Keep in mind this only fires on state changes, so when the video ends, pauses, etc
					trackVideoEvent();
					clearInterval(interval);
					setIntervalId(null);
				} else {
					clearInterval(interval);
					setIntervalId(null);
				}
			},
	}),
	withProps((props) => ({
		bindedOnStateChange: props.onStateChange, // Explicitly bind it to a prop
		bindedTrackVideoEvent: props.trackVideoEvent,
	})),

	withHandlers({
		onYouTubeIframeAPIReady:
			({ setPlayer, iframeRef, bindedOnStateChange, setVideoDuration }) =>
			() => {
				// If the iframe doesn't exist, or the player isn't created return
				if (!iframeRef.current || !window.YT) return;

				// Binds iframeRef to the iframe and manages player control assignment
				iframeRef.current.id = `youtube-player-${Math.random()}`;
				const newPlayer = new window.YT.Player(iframeRef.current.id, {
					events: {
						onReady: (event) => {
							setVideoDuration(event.target.getDuration()); // When the video loads, set the total video duration for tracking event
						},
						onStateChange: bindedOnStateChange, // Use the explicitly bound function
					},
				});
				setPlayer(newPlayer);
			},
	}),

	lifecycle({
		// Create a script tag to use the Youtube Iframe Player API/make the iframe a controlled component
		componentDidMount() {
			if (!window.YT) {
				const tag = document.createElement('script');
				tag.src = 'https://www.youtube.com/iframe_api';
				document.body.appendChild(tag);
				window.onYouTubeIframeAPIReady =
					this.props.onYouTubeIframeAPIReady;
			} else {
				this.props.onYouTubeIframeAPIReady();
			}
		},
		// Verifies interval stops firing on navigation
		componentWillUnmount() {
			clearInterval(this.props.interval);
		},
	})
);

const YoutubeVideo = ({ src, videoName, iframeRef }) => (
	<div className="ph-responsive-embed">
		<iframe
			title={videoName || 'video'}
			src={src}
			ref={iframeRef}
			allowFullScreen
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	</div>
);

YoutubeVideo.propTypes = {
	src: PropTypes.string.isRequired,
	videoName: PropTypes.string.isRequired,
	iframeRef: PropTypes.object,
};

export default enhance(YoutubeVideo);
